import React, { useEffect } from 'react';
import {
  CurrencyType,
  FulfillmentOrdersResponseDto,
  OrderDetailsAmountsDto,
  OrderDetailsPaymentsInfoDto,
  TransactionChargesResponseDto,
} from '@tiendanube/common';
import { Status as PaymentStatusEnum } from '@tiendanube/common/src/enums';
import FeatureFlag from 'App/components/FeatureFlag';
import { TOTAL_PAID_ORDER_DETAIL } from 'App/featuresFlags';
import { useBusinessData } from 'domains/Account/hooks';
import {
  OrderMerchantChargeAmountRow,
  OrderMerchantChargesInProgressRow,
  OrderRefundedMerchantChargeRow,
  OrderTotalInProgressRow,
} from 'domains/Orders/components';
import { useIsEditOrdersEnabled } from 'domains/Orders/Orders/hooks';
import {
  useOrderDiscounts,
  useShowMerchantCharges,
} from 'domains/Orders/Orders/pages/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import AmountRow from './components/AmountRow';
import ShippingCosts from '../components/ShippingCosts';

interface PaymentsInfoCardAmountsProps {
  readonly amounts: OrderDetailsAmountsDto;
  readonly paymentsInfo: OrderDetailsPaymentsInfoDto;
  readonly currency: CurrencyType;
  readonly paymentMethod: string;
  readonly fulfillmentOrders?: FulfillmentOrdersResponseDto[];
  readonly totalProducts: number;
  readonly isEdited?: boolean;
  readonly charges: TransactionChargesResponseDto;
  readonly gateway?: string;
  readonly paymentCreationDate?: string;
  readonly paymentStatus: PaymentStatusEnum;
}

function PaymentsInfoCardAmounts({
  amounts,
  paymentsInfo,
  currency,
  paymentMethod,
  fulfillmentOrders,
  totalProducts,
  isEdited,
  charges,
  gateway,
  paymentCreationDate,
  paymentStatus,
}: Readonly<PaymentsInfoCardAmountsProps>): React.JSX.Element {
  const {
    shipping,
    previousShippingOwner,
    shippingDetail,
    totalPaid,
    otherCosts,
    paidByCustomer,
    totalDiffPending,
  } = amounts;

  const { subtotal, total, refundedAmount, paidTotalAfterRefund, netTotal } =
    paymentsInfo;

  const t = useTranslationOrders('orderSummaryCard');
  const hasEditOrders = useIsEditOrdersEnabled();
  const { fetchBusinessData } = useBusinessData();

  const discounts = useOrderDiscounts(amounts, paymentMethod);
  const {
    shouldShowMerchantCharges,
    isMerchantChargeInProgress,
    hasRefundedMerchantCharges,
  } = useShowMerchantCharges(charges, gateway, paymentCreationDate);

  useEffect(() => {
    fetchBusinessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizedShipping = shipping ?? 0;

  function totalLabel() {
    if (paymentStatus === PaymentStatusEnum.PAID && !refundedAmount) {
      return t('paymentsInfo.customerPaidTotal');
    }
    return t('paymentsInfo.total');
  }

  return (
    <>
      <AmountRow
        id="subtotal"
        title={t('tableAmounts.subtotal', {
          count: totalProducts,
        })}
        amount={subtotal}
      />

      {(normalizedShipping >= 0 || !!fulfillmentOrders) && (
        <ShippingCosts
          shippingDetail={shippingDetail}
          shipping={normalizedShipping}
          previousShipping={previousShippingOwner}
          currency={currency}
          fulfillmentOrders={fulfillmentOrders}
        />
      )}

      {discounts.map((discount) => (
        <AmountRow
          key={discount.id}
          id={discount.id}
          title={discount.name}
          amount={{ value: String(discount.value), currency: currency }}
          isNegative={discount.appearance === 'danger'}
        />
      ))}

      <AmountRow id="total" title={totalLabel()} amount={total} isStrong />

      {!!refundedAmount && (
        <AmountRow
          id="refundedAmount"
          title={t('paymentsInfo.refundedAmount')}
          amount={refundedAmount}
          isNegative
          hideIfZero
        />
      )}

      {!!paidTotalAfterRefund && (
        <AmountRow
          id="paidTotalAfterRefund"
          title={t('paymentsInfo.paidTotalAfterRefund')}
          amount={paidTotalAfterRefund}
          isStrong
        />
      )}

      {shouldShowMerchantCharges() && (
        <>
          {!isEdited && (
            <>
              {isMerchantChargeInProgress(paymentStatus) && (
                <OrderMerchantChargesInProgressRow />
              )}

              {(charges.merchantCharges ?? []).map((charge) => (
                <OrderMerchantChargeAmountRow
                  key={charge.type}
                  transactionCharge={charge}
                />
              ))}

              {hasRefundedMerchantCharges() && (
                // provisional for version 1
                <OrderRefundedMerchantChargeRow
                  refundedMerchantChargesTotal={
                    charges.refundedMerchantChargesTotal
                  }
                  defaultCurrency={currency}
                  gateway={gateway}
                />
              )}
            </>
          )}

          {isMerchantChargeInProgress(paymentStatus) && (
            <OrderTotalInProgressRow />
          )}

          {!isMerchantChargeInProgress(paymentStatus) && !!netTotal && (
            <AmountRow
              id="netTotal"
              title={t('paymentsInfo.netTotal')}
              amount={netTotal}
              isStrong
            />
          )}
        </>
      )}

      {hasEditOrders && isEdited && (
        <>
          <AmountRow
            id="paidBycustomer"
            title={t('tableAmounts.paidBycustomer')}
            amount={{
              value: String(paidByCustomer),
              currency: currency,
            }}
          />

          <AmountRow
            id="totalDiff"
            title={
              totalDiffPending < 0
                ? t('tableAmounts.feeToPay')
                : t('tableAmounts.feeToCharge')
            }
            amount={{
              value: String(Math.abs(totalDiffPending)),
              currency: currency,
            }}
            isStrong
          />
        </>
      )}

      <FeatureFlag flag={TOTAL_PAID_ORDER_DETAIL}>
        {!!totalPaid && !!otherCosts && (
          <>
            <AmountRow
              id="otherCosts"
              title={t('tableAmounts.otherCosts')}
              amount={{ value: String(otherCosts), currency: currency }}
            />

            <AmountRow
              id="totalPaid"
              title={t('tableAmounts.totalPaid')}
              amount={{ value: String(totalPaid), currency: currency }}
              isStrong
            />
          </>
        )}
      </FeatureFlag>
    </>
  );
}

export default PaymentsInfoCardAmounts;
